import View from "./view.js";
import { esc } from "../escape.js";
import { itemImageSrc } from "../itemVisual.js";
import { formatMoney } from "../currency.js";

// Device-level display preference (like theme) - which image style the New
// Order grid shows. Emoji is the default: a faster, more playful read for
// staff running the register, with photos available for anyone who prefers
// real product shots. Never synced to businesses.settings - purely local.
const VISUAL_MODE_KEY = "pointbunny_pos_visual_mode";

class NewOrderView extends View {
  _parentElement = document.querySelector(".modal-parent");
  _openBtn = document.getElementById("newOrderBtn");
  // Set while adding items to an open table tab (2026-10-22): { tableName,
  // tabTotal }. The cart then holds only the new items, and Checkout becomes
  // "Send to kitchen" with "Save to tab" beside it.
  _tabOrder = null;

  _getVisualMode() {
    return localStorage.getItem(VISUAL_MODE_KEY) === "photo" ? "photo" : "emoji";
  }

  _generateMarkUp() {
    const cap = (s) => s[0].toUpperCase() + s.slice(1);
    const { menuItems, menuCategories, cart } = this._data;

    const cartCount = cart.length;
    const cartTotal = cart.reduce((acc, item) => acc + item.totalPrice, 0);
    const userCategories = menuCategories.filter((c) => c !== "uncategorized");

    const statuses = this._stockStatuses();
    this._stockStatusCache = statuses; // popover content lookup on chip click
    const visualMode = this._getVisualMode();
    const renderCard = (item) => {
      const unavailable = item.status === "unavailable";
      // Stock never blocks a sale - an alert chip only, card stays clickable.
      // When an item is BOTH unavailable and low/out, unavailable wins (it
      // already blocks clicks and has its own badge).
      const st = !unavailable ? statuses.get(item._id) : null;
      const src = itemImageSrc(item, { emojiOnly: visualMode === "emoji" });
      const imgMarkup = `<img src="${esc(src)}" alt="${esc(item.itemName)}" class="pos-item-img" loading="lazy" onerror="this.alt='';this.className='pos-item-img pos-item-img--ph';this.removeAttribute('src')">`;
      return `
        <div class="pos-item-card${unavailable ? " pos-item-card--unavailable" : ""}" data-id="${item._id}">
          ${imgMarkup}
          ${unavailable ? '<span class="pos-item-badge">Unavailable</span>' : ""}
          ${st ? this._stockChipHtml(st) : ""}
          <div class="pos-item-info">
            <div class="pos-item-name">${esc(item.itemName)}</div>
            <div class="pos-item-price">${formatMoney(item.price)}</div>
          </div>
        </div>`;
    };

    const activeCategories = userCategories.filter((cat) =>
      menuItems.some((i) => i.category === cat && i.status !== "inactive"),
    );

    const uncategorizedItems = menuItems.filter(
      (i) =>
        i.status !== "inactive" &&
        (!i.category ||
          i.category === "uncategorized" ||
          !menuCategories.includes(i.category)),
    );

    const tabs = [
      `<button class="pos-cat-tab pos-cat-tab--active" data-cat="all">All Items</button>`,
      ...activeCategories.map(
        (cat) =>
          `<button class="pos-cat-tab" data-cat="${esc(cat)}">${esc(cap(cat))}</button>`,
      ),
      uncategorizedItems.length
        ? `<button class="pos-cat-tab" data-cat="uncategorized">Uncategorized</button>`
        : "",
    ].join("");

    // "All Items" is a single flat grid (no category headers); category sections
    // start hidden and are revealed by their tab.
    const allActiveItems = menuItems.filter((i) => i.status !== "inactive");
    const allItemsGroup = `
      <div class="pos-cat-section" data-section="__all">
        <div class="pos-items-grid">${allActiveItems.map(renderCard).join("")}</div>
      </div>`;

    const categoryGroups = activeCategories
      .map((cat) => {
        const items = menuItems.filter(
          (i) => i.category === cat && i.status !== "inactive",
        );
        if (!items.length) return "";
        return `
          <div class="pos-cat-section hidden" data-section="${esc(cat)}">
            <p class="pos-cat-label">${esc(cap(cat))}</p>
            <div class="pos-items-grid">${items.map(renderCard).join("")}</div>
          </div>`;
      })
      .join("");

    const uncatGroup = uncategorizedItems.length
      ? `
        <div class="pos-cat-section hidden" data-section="uncategorized">
          <p class="pos-cat-label pos-cat-label--muted">Uncategorized</p>
          <div class="pos-items-grid">${uncategorizedItems.map(renderCard).join("")}</div>
        </div>`
      : "";

    const cartMarkup = this._cartRowsMarkup(cart);

    return `
      <div class="pos-screen" id="newOrderModal">
        <div class="pos-header">
          <button class="modal-close pos-close-btn" aria-label="Close">×</button>
          <div class="pos-visual-toggle" data-mode="${visualMode}" role="group" aria-label="Item image style">
            <span class="pos-visual-toggle-thumb" aria-hidden="true"></span>
            <button type="button" class="pos-visual-toggle-btn${visualMode === "emoji" ? " pos-visual-toggle-btn--active" : ""}" data-mode="emoji" title="Show emoji" aria-pressed="${visualMode === "emoji"}"><span aria-hidden="true">😊</span><span class="pos-visual-toggle-label">Emoji</span></button>
            <button type="button" class="pos-visual-toggle-btn${visualMode === "photo" ? " pos-visual-toggle-btn--active" : ""}" data-mode="photo" title="Show photos" aria-pressed="${visualMode === "photo"}"><span aria-hidden="true">🖼️</span><span class="pos-visual-toggle-label">Photos</span></button>
          </div>
          <h2 class="pos-title">${this._tabOrder ? `Table ${esc(this._tabOrder.tableName)} · add items` : "New Order"}</h2>
          <button class="btn-checkout pos-cart-fab">
            Cart${cartCount > 0 ? ` <span class="pos-cart-count">${cartCount}</span>` : ""} · ${formatMoney(cartTotal)}
          </button>
        </div>
        <div class="pos-body">
          ${menuItems.length > 0 ? `<div class="pos-cat-sidebar">${tabs}</div>` : ""}
          <div class="pos-menu">
            ${
              menuItems.length === 0
                ? `<div class="pos-empty">
                    <span class="pos-empty-emoji" aria-hidden="true">📦</span>
                    <p class="pos-empty-title">No items yet</p>
                    <p class="pos-empty-sub">Add your items to start taking orders.</p>
                    <button class="btn primary pos-empty-add" type="button">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><path d="M8 12h8"/><path d="M12 8v8"/></svg>
                      Add Items
                    </button>
                  </div>`
                : `<div class="pos-items-area" id="posItemsArea">
                  ${allItemsGroup}${categoryGroups}${uncatGroup}
                </div>`
            }
          </div>
          <div class="pos-cart-sidebar">
            <h3 class="pos-cart-title">${this._tabOrder ? `New for table ${esc(this._tabOrder.tableName)}` : "Cart"}</h3>
            ${this._tabOrder ? `<p class="pos-tab-note">Already on the tab: ${formatMoney(this._tabOrder.tabTotal)}</p>` : ""}
            <div class="pos-cart-list" id="cartItems">
              ${cartMarkup || '<p class="pos-cart-empty">No items yet.</p>'}
            </div>
            <div class="pos-cart-footer">
              <div class="pos-cart-total">
                <span>Total</span>
                <span>${formatMoney(cartTotal)}</span>
              </div>
              ${this._tabOrder
                ? `<div class="pos-tab-actions"><button class="pos-tab-save-btn" type="button">Save to tab</button><button class="btn-checkout pos-checkout-btn">Send to kitchen</button></div>`
                : `<button class="btn-checkout pos-checkout-btn">Checkout</button>`}
            </div>
          </div>
        </div>
      </div>`;
  }

  _cartRowsMarkup(cart) {
    return cart
      .map((item, i) => {
        const variants = item.selectedVariants.map((v) => v.variantName).join(", ");
        return `
          <div class="pos-cart-item cart-item-row">
            <div class="pos-cart-item-info">
              <span class="pos-cart-item-name">${esc(item.itemName)} ×${item.quantity}</span>
              ${variants ? `<span class="pos-cart-item-variants">${esc(variants)}</span>` : ""}
            </div>
            <div class="pos-cart-item-right">
              <span class="pos-cart-item-price">${formatMoney(item.totalPrice)}</span>
              <button class="cart-item-delete-btn pos-cart-delete" data-cart-index="${i}" type="button">×</button>
            </div>
          </div>`;
      })
      .join("");
  }

  // Surgical cart update: patches ONLY the cart list, total, and header pill.
  // The item grid + category tabs are untouched, so cart changes no longer
  // re-render (and "blink") the whole POS screen or reset the active tab.
  // Returns false if the POS screen isn't in the DOM (caller falls back to render).
  updateCart({ added = false } = {}) {
    const screen = this._parentElement.querySelector("#newOrderModal");
    if (!screen || !this._data) return false;
    const { cart } = this._data;
    const total = cart.reduce((acc, item) => acc + item.totalPrice, 0);

    const list = screen.querySelector("#cartItems");
    if (list) list.innerHTML = this._cartRowsMarkup(cart) || '<p class="pos-cart-empty">No items yet.</p>';

    const totalEl = screen.querySelector(".pos-cart-total span:last-child");
    if (totalEl) totalEl.textContent = formatMoney(total);

    const fab = screen.querySelector(".pos-cart-fab");
    if (fab) fab.innerHTML = `Cart${cart.length > 0 ? ` <span class="pos-cart-count">${cart.length}</span>` : ""} · ${formatMoney(total)}`;

    if (added && list) {
      list.scrollTop = list.scrollHeight;
      const last = list.querySelector(".cart-item-row:last-child");
      if (last) last.classList.add("cart-item--new", "cart-item--added");
    }
    return true;
  }

  _stockStatusCache = new Map(); // product id -> { state, out, low } for the popover

  // Per-product stock status computed from the state slices passed into
  // render(): which linked inventory items are out (qty <= 0) and which are
  // low (at/below their alert threshold). Mirrors model.productStockStatus.
  _stockStatuses() {
    const { inventoryItems = [], inventoryLinks = [] } = this._data ?? {};
    const map = new Map();
    inventoryLinks.forEach((l) => {
      if (!(Number(l.qty) > 0)) return; // a "None" (qty 0) override never drives alerts
      const inv = inventoryItems.find((i) => i.id === l.inventoryItemId);
      if (!inv) return;
      const isOut = inv.quantity <= 0;
      const isLow = !isOut && inv.lowStockThreshold != null && inv.quantity <= inv.lowStockThreshold;
      if (!isOut && !isLow) return;
      const st = map.get(l.menuItemId) ?? { out: [], low: [] };
      const bucket = isOut ? st.out : st.low;
      if (!bucket.includes(inv.name)) bucket.push(inv.name);
      map.set(l.menuItemId, st);
    });
    map.forEach((st) => { st.state = st.out.length ? "out" : "low"; });
    return map;
  }

  // Compact alert chip: "● 1 OUT · ● 1 LOW ›". Tapping it opens the
  // per-item alerts popover; the chip is a button so it never triggers the
  // card's open-item click (guarded in newOrderItemView).
  _stockChipHtml(st) {
    const seg = [];
    if (st.out.length) seg.push(`<span class="pos-chip-dot pos-chip-dot--out"></span>${st.out.length} OUT`);
    if (st.low.length) seg.push(`<span class="pos-chip-dot pos-chip-dot--low"></span>${st.low.length} LOW`);
    return `
      <button type="button" class="pos-stock-chip" aria-label="Inventory alerts">
        ${seg.join('<span class="pos-chip-sep">·</span>')}
        <span class="pos-chip-caret">&rsaquo;</span>
      </button>`;
  }

  // One shared popover on <body> (cards clip overflow, tooltip.js pattern).
  _stockPop = null;
  _ensureStockPop() {
    if (this._stockPop) return this._stockPop;
    const pop = document.createElement("div");
    pop.className = "pos-stock-pop hidden";
    document.body.appendChild(pop);
    document.addEventListener("click", (e) => {
      if (!e.target.closest(".pos-stock-pop") && !e.target.closest(".pos-stock-chip")) {
        pop.classList.add("hidden");
      }
    });
    this._stockPop = pop;
    return pop;
  }

  _openStockPop(chip, st) {
    const pop = this._ensureStockPop();
    const row = (name, state) => `
      <div class="pos-stock-pop-row">
        <span class="pos-stock-pop-name">${esc(name)}</span>
        <span class="pos-stock-pop-pill pos-stock-pop-pill--${state}">${state === "out" ? "Out of stock" : "Low stock"}</span>
      </div>`;
    pop.innerHTML = `
      <p class="pos-stock-pop-title">Inventory alerts</p>
      ${st.out.map((n) => row(n, "out")).join("")}
      ${st.low.map((n) => row(n, "low")).join("")}`;
    pop.classList.remove("hidden");
    // Position under the chip, viewport-clamped.
    const r = chip.getBoundingClientRect();
    const w = pop.offsetWidth;
    let left = r.left + r.width / 2 - w / 2;
    left = Math.max(8, Math.min(left, window.innerWidth - w - 8));
    let top = r.bottom + 6;
    if (top + pop.offsetHeight > window.innerHeight - 8) top = r.top - pop.offsetHeight - 6;
    pop.style.left = `${left}px`;
    pop.style.top = `${top}px`;
  }

  // Chip clicks (delegated on the persistent parent, survives re-renders).
  _addHandlerStockChip() {
    this._parentElement.addEventListener("click", (e) => {
      const chip = e.target.closest(".pos-stock-chip");
      if (!chip) return;
      e.stopPropagation();
      const pop = this._ensureStockPop();
      if (!pop.classList.contains("hidden") && this._popChip === chip) {
        pop.classList.add("hidden");
        return;
      }
      this._popChip = chip;
      const id = chip.closest(".pos-item-card")?.dataset.id;
      const st = this._stockStatusCache.get(id);
      if (st) this._openStockPop(chip, st);
    });
  }

  // Surgical chip patch after stock changes (sale, void, restock, sync from
  // another terminal). Takes the Map from model.productStockStatuses(). No-op
  // when the order screen isn't rendered - never a full re-render (no-blink).
  updateStockBadges(statuses) {
    this._stockStatusCache = statuses;
    this._stockPop?.classList.add("hidden"); // content may be stale
    const screen = this._parentElement.querySelector("#newOrderModal");
    if (!screen) return;
    screen.querySelectorAll(".pos-item-card").forEach((card) => {
      const unavailable = card.classList.contains("pos-item-card--unavailable");
      const st = !unavailable ? statuses.get(card.dataset.id) : null;
      card.querySelector(".pos-stock-chip")?.remove();
      if (st) {
        card.querySelector(".pos-item-img")?.insertAdjacentHTML("afterend", this._stockChipHtml(st));
      }
    });
  }

  // Slide a cart row out before it's deleted; resolves when the exit animation
  // has played (immediately if the row isn't found).
  animateCartRowOut(index) {
    return new Promise((resolve) => {
      const row = this._parentElement.querySelectorAll("#cartItems .cart-item-row")[index];
      if (!row) { resolve(); return; }
      row.classList.add("cart-item--removing");
      setTimeout(resolve, 180);
    });
  }

  _addHandlerCategoryTabs() {
    this._parentElement.addEventListener("click", (e) => {
      const tab = e.target.closest(".pos-cat-tab");
      if (!tab) return;

      this._parentElement
        .querySelectorAll(".pos-cat-tab")
        .forEach((t) => t.classList.remove("pos-cat-tab--active"));
      tab.classList.add("pos-cat-tab--active");

      const cat = tab.dataset.cat;
      this._parentElement.querySelectorAll(".pos-cat-section").forEach((s) => {
        const show = cat === "all" ? s.dataset.section === "__all" : s.dataset.section === cat;
        s.classList.toggle("hidden", !show);
      });

      const itemsArea = this._parentElement.querySelector(".pos-items-area");
      if (itemsArea) itemsArea.scrollTop = 0;
    });
  }

  // Emoji/Photo display toggle - surgically patches image srcs in place
  // (like updateCart/updateStockBadges) rather than a full render(), so
  // switching modes never replays the screen's entrance animation.
  _addHandlerToggleVisualMode() {
    this._parentElement.addEventListener("click", (e) => {
      const btn = e.target.closest(".pos-visual-toggle-btn");
      if (!btn) return;
      const mode = btn.dataset.mode;
      if (mode === this._getVisualMode()) return;
      localStorage.setItem(VISUAL_MODE_KEY, mode);
      this._applyVisualMode(mode);
    });
  }

  _applyVisualMode(mode) {
    const screen = this._parentElement.querySelector("#newOrderModal");
    if (!screen) return;
    screen.querySelector(".pos-visual-toggle")?.setAttribute("data-mode", mode);
    screen.querySelectorAll(".pos-visual-toggle-btn").forEach((b) => {
      const active = b.dataset.mode === mode;
      b.classList.toggle("pos-visual-toggle-btn--active", active);
      b.setAttribute("aria-pressed", String(active));
    });
    const menuItems = this._data?.menuItems ?? [];
    screen.querySelectorAll(".pos-item-card").forEach((card) => {
      const item = menuItems.find((i) => i._id === card.dataset.id);
      const img = card.querySelector(".pos-item-img");
      if (!item || !img) return;
      img.src = itemImageSrc(item, { emojiOnly: mode === "emoji" });
    });
  }

  _addHandlerDeleteCartItem(handler) {
    this._parentElement.addEventListener("click", (e) => {
      const btn = e.target.closest(".cart-item-delete-btn");
      if (!btn) return;
      handler(Number(btn.dataset.cartIndex));
    });
  }

  _addHandlerShowMenuModal(handler) {
    this._openBtn.addEventListener("click", function (e) {
      e.preventDefault();
      handler();
    });
  }

  // "Add Items" button in the empty-catalogue state of the New Order screen.
  // Delegated on the persistent parent so it works each time the modal re-renders.
  // Heads-up shown when an out-of-stock product lands in the cart. Purely
  // informational - the item is already added and the sale is never blocked;
  // Continue (or a backdrop click via modalBackdrop.js) just dismisses.
  showStockNotice(names) {
    const modal = document.getElementById("stockNoticeModal");
    const msg = document.getElementById("stockNoticeMsg");
    if (!modal || !msg || !names?.length) return;
    const list = names.length > 1
      ? `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`
      : names[0];
    msg.innerHTML = `<strong>${esc(list)}</strong> ${names.length > 1 ? "are" : "is"} currently out of stock.`;
    if (!this._stockNoticeWired) {
      this._stockNoticeWired = true;
      document.getElementById("stockNoticeContinue")?.addEventListener("click", () => modal.classList.add("hidden"));
    }
    modal.classList.remove("hidden");
  }

  _addHandlerEmptyAddItems(handler) {
    this._parentElement.addEventListener("click", (e) => {
      if (!e.target.closest(".pos-empty-add")) return;
      handler();
    });
  }

  _addHandlerSaveToTab(handler) {
    this._parentElement.addEventListener("click", (e) => {
      if (e.target.closest(".pos-tab-save-btn")) handler();
    });
  }

  // Closes the order screen without the discard prompt (after a tab send).
  closeNow() {
    const screen = this._parentElement.querySelector(".pos-screen");
    if (screen) screen.classList.add("modal-exiting");
    setTimeout(() => { this._parentElement.innerHTML = ""; }, 220);
  }

  _addHandlerCloseModal(handler) {
    this._parentElement.addEventListener("click", (e) => {
      const btn = e.target.closest(".modal-close");
      if (!btn || !btn.closest("#newOrderModal")) return;
      const screen = this._parentElement.querySelector(".pos-screen");
      handler(() => {
        if (screen) screen.classList.add("modal-exiting");
        setTimeout(() => {
          this._parentElement.innerHTML = "";
        }, 220);
      });
    });
  }
}

export default new NewOrderView();
